import { useLanguage } from '@/i18n/LanguageContext'
import { useServiceStatus, type ServiceStatus } from '@/api/hooks/useEngagement'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  HeartPulse,
  Server,
  Code2,
  Globe,
  Webhook,
  RefreshCw,
  Database,
  Cloud,
  CheckCircle2,
  AlertTriangle,
  Wrench,
  XCircle,
} from 'lucide-react'

const serviceIcons: Record<string, React.ElementType> = {
  'Express Server': Server,
  'Laravel API': Code2,
  'Meta Graph API': Globe,
  'Webhook': Webhook,
  'Comment Polling': RefreshCw,
  'Data Storage': Database,
  'Cloudflare R2': Cloud,
}

const friendlyNames: Record<string, string> = {
  'Express Server': 'Servidor Principal',
  'Laravel API': 'Sistema de Login',
  'Meta Graph API': 'Conexao com Facebook/Instagram',
  'Webhook': 'Recebimento de Mensagens',
  'Comment Polling': 'Leitura de Comentarios',
  'Data Storage': 'Banco de Dados',
  'Cloudflare R2': 'Armazenamento de Arquivos',
}

const friendlyDetails: Record<string, Record<string, string>> = {
  'Express Server': { 'Running': 'Funcionando normalmente' },
  'Laravel API': { 'Auth endpoint responding': 'Respondendo normalmente', 'HTTP 401': 'Respondendo normalmente' },
  'Meta Graph API': { 'Token valid': 'Conectado ao Meta', 'Token expired': 'Token expirado', 'No token available to test': 'Nenhuma conta vinculada para testar' },
  'Webhook': { 'No active subscriptions': 'Sem inscricoes ativas', 'No page to check subscription': 'Nenhuma pagina para verificar' },
  'Comment Polling': { 'Paused': 'Pausado temporariamente', 'Active (every 30s)': 'Verificando a cada 30 segundos' },
  'Data Storage': {},
  'Cloudflare R2': { 'Bucket connected': 'Conectado', 'S3 client not configured': 'Nao configurado' },
}

const statusConfig = {
  operational: { label: 'Funcionando', color: 'bg-emerald-500', textColor: 'text-emerald-500', bgLight: 'bg-emerald-500/10', icon: CheckCircle2 },
  degraded: { label: 'Instavel', color: 'bg-yellow-500', textColor: 'text-yellow-500', bgLight: 'bg-yellow-500/10', icon: AlertTriangle },
  maintenance: { label: 'Em manutencao', color: 'bg-orange-500', textColor: 'text-orange-500', bgLight: 'bg-orange-500/10', icon: Wrench },
  offline: { label: 'Fora do ar', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-500/10', icon: XCircle },
}

function translateDetails(serviceName: string, details?: string): string | undefined {
  if (!details) return undefined
  const map = friendlyDetails[serviceName]
  if (map && map[details]) return map[details]
  if (details.startsWith('clients.json readable')) {
    const match = details.match(/\((\d+)KB\)/)
    return match ? `Funcionando (${match[1]}KB)` : 'Funcionando'
  }
  if (details.startsWith('Subscribed to')) return 'Inscrito e recebendo eventos'
  if (details.startsWith('Active (every')) {
    const match = details.match(/every (\d+)s/)
    return match ? `Verificando a cada ${match[1]} segundos` : 'Ativo'
  }
  return details
}

function ServiceCard({ service }: { service: ServiceStatus }) {
  const cfg = statusConfig[service.status] || statusConfig.offline
  const Icon = serviceIcons[service.name] || Server
  const StatusIcon = cfg.icon
  const name = friendlyNames[service.name] || service.name
  const details = translateDetails(service.name, service.details)

  return (
    <div className="group relative overflow-hidden rounded-xl border border-border bg-card p-4 transition-all hover:border-border/80">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`shrink-0 rounded-lg p-2 ${cfg.bgLight}`}>
            <Icon className={`h-5 w-5 ${cfg.textColor}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold">{name}</h3>
            {details && (
              <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-1">{details}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Badge variant="outline" className={`text-[10px] font-medium ${cfg.textColor} border-current/20`}>
            <StatusIcon className="mr-1 h-3 w-3" />
            {cfg.label}
          </Badge>
          {service.responseTime > 0 && (
            <span className="text-[10px] text-muted-foreground">{service.responseTime}ms</span>
          )}
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 right-0 h-[2px] ${cfg.color}`} />
    </div>
  )
}

export function Status() {
  const { data, isLoading, dataUpdatedAt, isFetching } = useServiceStatus()

  const lastUpdate = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')
    : '—'

  const operationalCount = data?.services?.filter(s => s.status === 'operational').length || 0
  const totalCount = data?.services?.length || 0
  const allOperational = operationalCount === totalCount && totalCount > 0

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(7)].map((_, i) => <Skeleton key={i} className="h-[88px] rounded-xl" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <HeartPulse className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-bold">Status dos Servicos</h1>
          </div>
          <p className="text-sm text-muted-foreground">Veja se tudo esta funcionando normalmente</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isFetching && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />}
            <span className="text-[10px] text-muted-foreground">Atualizado: {lastUpdate}</span>
          </div>
          <Badge variant={allOperational ? 'default' : 'destructive'} className="text-[10px]">
            {operationalCount}/{totalCount} ok
          </Badge>
        </div>
      </div>

      {/* Overall status banner */}
      {allOperational ? (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <span className="text-sm font-medium text-emerald-500">
            Tudo funcionando normalmente
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-xl border border-yellow-500/20 bg-yellow-500/5 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <span className="text-sm font-medium text-yellow-500">
            Alguns servicos estao com problema
          </span>
        </div>
      )}

      {/* Service cards grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data?.services?.map((service) => (
          <ServiceCard key={service.name} service={service} />
        ))}
      </div>

      {/* Footer info */}
      <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground/50">
        <RefreshCw className="h-3 w-3" />
        <span>Atualiza automaticamente a cada 1 minuto</span>
      </div>
    </div>
  )
}
